import { sessionState } from '../../state';
import { logCodexSteeringDebug } from '../session-debug';
import { captureInitialRenderScrollRestoreSnapshot, restoreInitialRenderScrollPosition } from '../chat-scroll-restore';
import { buildWelcomeMarkup } from './context';
import { getMessagesDiv } from './dom';
import { clearPreviewCode } from './markdown';
import { createMsgElement } from './message-element';
import { renderAssistantSegments } from './assistant-segments';
import { getRenderEpoch, nextRenderEpoch } from './render-epoch';
import { scrollToBottom, updateScrollbar } from './scrollbar';
import type { ChatMessage, RenderMessagesOptions } from './types';

const LARGE_RENDER_THRESHOLD = 100;
const MEDIUM_RENDER_THRESHOLD = 50;
const LARGE_RENDER_BATCH_SIZE = 15;
const MEDIUM_RENDER_BATCH_SIZE = 12;
const SMALL_RENDER_BATCH_SIZE = 10;
const LARGE_RENDER_BATCH_DELAY_MS = 24;
const MEDIUM_RENDER_BATCH_DELAY_MS = 20;
const SMALL_RENDER_BATCH_DELAY_MS = 16;

function getRenderBatchTuning(messageCount: number): { batchSize: number; batchDelay: number } {
  // Render newest messages first, then fill older history in background-sized batches.
  if (messageCount > LARGE_RENDER_THRESHOLD) {
    return { batchSize: LARGE_RENDER_BATCH_SIZE, batchDelay: LARGE_RENDER_BATCH_DELAY_MS };
  }
  if (messageCount > MEDIUM_RENDER_THRESHOLD) {
    return { batchSize: MEDIUM_RENDER_BATCH_SIZE, batchDelay: MEDIUM_RENDER_BATCH_DELAY_MS };
  }
  return { batchSize: SMALL_RENDER_BATCH_SIZE, batchDelay: SMALL_RENDER_BATCH_DELAY_MS };
}

export function buildMsgElement(m: ChatMessage): HTMLElement {
  if (m.role !== 'assistant') return createMsgElement(m.role || 'system', m.content || '', m.attachments || [], m);
  const message = {
    ...m,
    model: m.model || '',
    displayModel: m.displayModel
      || m.model
      || '',
  };
  const el = createMsgElement('assistant', '');
  const identity = String(m.clientMessageId || m.timestamp || '').trim();
  if (identity) el.dataset.messageIdentity = identity;
  renderAssistantSegments(el.querySelector('.msg-bubble'), message);
  return el;
}

export function renderMessages(messages: ChatMessage[], options: RenderMessagesOptions = {}): void {
  const messagesDiv = getMessagesDiv();
  if (!messagesDiv) return;
  const scrollRestoreSnapshot = captureInitialRenderScrollRestoreSnapshot(
    messagesDiv,
    options.preserveViewport === true,
  );
  const epoch = nextRenderEpoch();
  logCodexSteeringDebug('render-start', {
    sessionId: sessionState.currentSessionId || null,
    epoch,
    messageCount: messages.length,
    messageOrder: messages.slice(-12).map((message) => ({
      role: message?.role || null,
      identity: message?.clientMessageId || message?.timestamp || null,
      contentLength: String(message?.content || '').length,
      segmentCount: Array.isArray(message?.segments) ? message.segments.length : 0,
    })),
    preserveViewport: options.preserveViewport === true,
    immediate: options.immediate === true,
  });
  clearPreviewCode();
  messagesDiv.classList.add('messages-switching');
  messagesDiv.innerHTML = '';
  if (messages.length === 0) {
    messagesDiv.innerHTML = buildWelcomeMarkup(sessionState.currentAgent);
    requestAnimationFrame(() => messagesDiv.classList.remove('messages-switching'));
    return;
  }
  if (options.immediate) {
    const frag = document.createDocumentFragment();
    messages.forEach((message) => frag.appendChild(buildMsgElement(message)));
    messagesDiv.appendChild(frag);
    scrollToBottom();
    requestAnimationFrame(() => messagesDiv.classList.remove('messages-switching'));
    return;
  }

  const len = messages.length;
  const { batchSize, batchDelay } = getRenderBatchTuning(len);

  const batches: number[][] = [];
  const firstBatchEnd = Math.min(batchSize, len);
  batches.push([len - firstBatchEnd, len]);

  let currentStart = len - firstBatchEnd;
  while (currentStart > 0) {
    const batchEnd = currentStart;
    const batchStart = Math.max(0, currentStart - batchSize);
    batches.push([batchStart, batchEnd]);
    currentStart = batchStart;
  }

  const frag0 = document.createDocumentFragment();
  for (let i = batches[0][0]; i < batches[0][1]; i += 1) frag0.appendChild(buildMsgElement(messages[i]));
  messagesDiv.appendChild(frag0);
  restoreInitialRenderScrollPosition(messagesDiv, scrollRestoreSnapshot);
  updateScrollbar();
  requestAnimationFrame(() => messagesDiv.classList.remove('messages-switching'));

  let batchIndex = 1;

  function renderNextBatch(): void {
    if (getRenderEpoch() !== epoch || batchIndex >= batches.length) return;

    const [start, end] = batches[batchIndex];
    const prevHeight = messagesDiv.scrollHeight;
    const prevScrollTop = messagesDiv.scrollTop;
    const wasAtBottom = prevScrollTop + messagesDiv.clientHeight >= prevHeight - 5;

    const frag = document.createDocumentFragment();
    for (let i = start; i < end; i += 1) frag.appendChild(buildMsgElement(messages[i]));
    messagesDiv.insertBefore(frag, messagesDiv.firstChild);

    if (wasAtBottom) {
      messagesDiv.scrollTop = messagesDiv.scrollHeight - messagesDiv.clientHeight;
    } else {
      messagesDiv.scrollTop = prevScrollTop + (messagesDiv.scrollHeight - prevHeight);
    }
    updateScrollbar();

    batchIndex += 1;
    if (batchIndex < batches.length) {
      requestAnimationFrame(renderNextBatch);
    }
  }

  if (batches.length > 1) {
    setTimeout(() => requestAnimationFrame(renderNextBatch), batchDelay);
  }
}
